import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/api";
import { TRACK_IMAGES, LESSON_IMAGES } from "@/lib/images";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import Logo, { LogoMark } from "@/components/Logo";
import ShareApp from "@/components/ShareApp";
import RegionSwitcher from "@/components/RegionSwitcher";
import { Eye, Landmark, Wrench, Leaf, GraduationCap, Users, Globe2, Award, Briefcase, ArrowRight, ChevronRight, MapPin, Loader2, ShieldCheck, LayoutDashboard, Building2, HeartHandshake, Sun, Stethoscope, CheckCircle2, Quote, Star, Sparkles, Video, Clock, Camera, Zap, School, HandHeart, Landmark as Bank, Wifi } from "lucide-react";

const ROLE_HOME = { trainee: "/app", assessor: "/assessor", admin: "/admin", superadmin: "/superadmin", employer: "/employer" };

const IMG = {
  classroom: "https://images.pexels.com/photos/35551059/pexels-photo-35551059.jpeg?auto=compress&cs=tinysrgb&w=1400",
  physician: "https://images.pexels.com/photos/5738735/pexels-photo-5738735.jpeg?auto=compress&cs=tinysrgb&w=1400",
  team: "https://images.pexels.com/photos/36035002/pexels-photo-36035002.jpeg?auto=compress&cs=tinysrgb&w=1400",
  screening: "https://images.pexels.com/photos/28141441/pexels-photo-28141441/free-photo-of-vision-screening-eye-screening-for-children.jpeg?auto=compress&dpr=1&h=900&w=1400",
  eyerEco: "https://ahrons18.sg-host.com/wp-content/uploads/2026/09/fscd-eye-03.webp",
  eyerHand: "https://ahrons18.sg-host.com/wp-content/uploads/2026/09/fscd-eye-07.webp",
  eyerElder: "https://ahrons18.sg-host.com/wp-content/uploads/2026/09/fscd-eye-08.webp",
  retina: "https://ahrons18.sg-host.com/wp-content/uploads/2026/09/fscd-eye-09.webp",
  solar: "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1500&q=85",
};

const MARQUEE = ["Vision Centers", "Clean Energy", "Local Training", "Certification", "Employment", "Self-Reliant Communities"];

const PILLARS = [
  { icon: <Sun size={26} />, badge: "Flagship Infrastructure Program", title: "Clean Energy for Clinics & Schools", desc: "Reliable solar power and battery backup for the facilities communities depend on — paired with local maintenance training so the lights stay on long after installation.", tint: "bg-amber-50 text-amber-700 border-amber-200" },
  { icon: <Camera size={26} />, badge: "Flagship Health Program", title: "Vision Screening & Vision Centers", desc: "We bring portable Eyer2 eye imaging into healthcare deserts — trained local teams capture images, connected clinicians review, and patients move into referral, glasses, and follow-up. A care pathway, not a one-time camp.", tint: "bg-teal-50 text-teal-700 border-teal-200" },
  { icon: <GraduationCap size={26} />, badge: "This Platform", title: "Training, Certification & Jobs", desc: "Role-based courses train local people as eye technicians, care coordinators and clinic staff. Graduates earn verifiable FSCD certificates and move into paid work, internships and volunteering — running the very services above.", tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

const FRAMEWORK = [
  { no: "01", label: "Our Vision", title: "Self-Reliant Communities.", body: "Inclusive, improved healthcare, education, economic opportunity for women, and local institutions enabling long-term sustainable development." },
  { no: "02", label: "Our Mission", title: "Community-Driven Development With Practical Support.", body: "Facilitating development in rural and underserved regions — including healthcare deserts — by expanding economic opportunity, improving access to health and education, strengthening local institutions, and promoting job training and workforce participation for women and marginalized populations." },
  { no: "03", label: "Our Purpose", title: "Charitable. Educational. Scientific.", body: "Organized exclusively for charitable, educational and scientific purposes under Section 501(c)(3) of the Internal Revenue Code." },
  { no: "04", label: "IRS Approval", title: "501(c)(3) Public Charity.", body: "IRS approval received April 14, 2026, based on review of mission, purpose, governance and planned programs. Federal EIN 41-4092047." },
];

const SIX_AREAS = [
  { icon: <Briefcase size={20} />, t: "Community Economic Development", d: "Income generation, microenterprise, vocational training, entrepreneurship." },
  { icon: <School size={20} />, t: "Education & Capacity Building", d: "Workshops, adult literacy, skills training, community education." },
  { icon: <Stethoscope size={20} />, t: "Healthcare Services", d: "Vision screening, public-health education, clean water, wellness programs." },
  { icon: <HandHeart size={20} />, t: "Women's Economic Empowerment", d: "Job training, leadership, entrepreneurship, workforce participation." },
  { icon: <Building2 size={20} />, t: "Institutional Strengthening", d: "Governance, management, planning and systems for local institutions." },
  { icon: <Wifi size={20} />, t: "Technology & Infrastructure", d: "Connectivity, digital tools, clean energy and essential infrastructure." },
];

const APPROACH = [
  ["Listen & Assess", "Understand needs, local strengths, institutions and priorities."],
  ["Build Partnerships", "Connect qualified organizations, specialists, funders and leaders."],
  ["Plan & Equip", "Define scope, resources, technology, responsibilities and safeguards."],
  ["Train Locally", "Build knowledge, skills, local workforce and retained expertise."],
  ["Implement", "Launch responsibly with community participation and partner support."],
  ["Measure & Sustain", "Review outcomes, strengthen ownership and support continuity."],
];

const CHAIN = [
  { no: "01 / SEE & LEARN", title: "When a Student Can See the Board, the Story Changes.", body: "A basic eye examination and appropriate glasses can affect far more than eyesight — they can change participation, confidence, and the ability to learn.", tags: ["Vision", "Education", "Confidence", "Opportunity"], img: IMG.classroom },
  { no: "02 / POWER & CARE", title: "When a Clinic Has Reliable Power, Care Can Stay Closer to Home.", body: "FSCD's clean-energy approach addresses practical barriers through appropriately planned solar power, battery backup, efficient equipment, and locally maintainable systems.", tags: ["Cold Chain", "Medical Equipment", "24/7 Power", "Local Access"], img: IMG.physician },
  { no: "03 / TRAIN & OWN", title: "When Local People Can Maintain the System, the Project Stops Being \u201cOutside Help.\u201d", body: "Local training, maintenance responsibility, documentation, and skills transfer — with particular attention to opportunities for women and locally based participants.", tags: ["Women in Technical Roles", "Maintenance", "Gram Sabha", "Ownership"], img: IMG.team },
];

const COURSES = [
  { title: "Eye Technician Fundamentals", vertical: "Healthcare", weeks: 6, img: TRACK_IMAGES["eye-technician"], live: true },
  { title: "Patient Care Coordinator", vertical: "Healthcare", weeks: 5, img: TRACK_IMAGES["patient-care-coordinator"], live: true },
  { title: "Medical Front Office & Admin", vertical: "Healthcare", weeks: 4, img: LESSON_IMAGES["fo-l1"], live: true },
  { title: "Financial Literacy Fundamentals", vertical: "FinTech", weeks: 4, img: LESSON_IMAGES["fo-l3"], live: false },
  { title: "Solar Technician Fundamentals", vertical: "Skilled Trades", weeks: 10, img: IMG.solar, live: false },
  { title: "Digital Banking & FinTech", vertical: "FinTech", weeks: 3, img: LESSON_IMAGES["fo-l2"], live: false },
  { title: "Vertical Farming Fundamentals", vertical: "Sustainability", weeks: 4, img: IMG.classroom, live: false },
  { title: "Medical Coding & Billing", vertical: "Healthcare", weeks: 8, img: LESSON_IMAGES["pc-l2"], live: false },
];

const TESTIMONIALS = [
  { name: "Priya S.", role: "Eye Technician trainee, Jaipur", quote: "I studied the lessons on my phone between shifts — even without signal. Passing the exam and holding a verifiable certificate changed how employers see me.", stars: 5 },
  { name: "Dr. Meera N.", role: "FSCD Assessor", quote: "The video practical reviews let me coach trainees in villages I could never visit weekly. Scoring rubrics keep every assessment fair and consistent.", stars: 5 },
  { name: "Ravi K.", role: "Front Office graduate, Lucknow", quote: "From enrollment to interview scheduling, everything happened in one app. I now manage registration at an FSCD vision center — and I'm applying to become a trainer.", stars: 5 },
];

const CENTERS = [
  { city: "New Delhi", name: "FSCD Delhi Community Center", programs: 3 },
  { city: "Jaipur", name: "FSCD Jaipur Vision Center Hub", programs: 2 },
  { city: "Lucknow", name: "FSCD Lucknow Skills Center", programs: 3 },
  { city: "Pune", name: "FSCD Pune Training Site", programs: 2 },
];

const PARTNERS = ["Community Health Alliance", "Gram Sabha Network", "Solar Futures Co-op", "District Eye Hospitals", "Skill India Partners", "Rural FinTech Forum"];

const STATS = [
  ["3", "Flagship Programs"], ["12", "Launch Courses"], ["100+", "Community Sites Planned"],
  ["7", "Eyer2 Imaging Modes"], ["12", "Languages"], ["13", "Launch Countries"],
];

const REGIONS = ["India", "Philippines", "Southeast Asia", "Europe", "Africa", "Americas"];

const DEMOS = [
  { role: "Student", email: "student@demo.org", icon: <GraduationCap size={22} />, desc: "Courses, quizzes, certificates, career pathway", testid: "demo-student" },
  { role: "Instructor", email: "instructor@demo.org", icon: <Users size={22} />, desc: "Course builder & practical review console", testid: "demo-instructor" },
  { role: "Program Admin", email: "admin@demo.org", icon: <LayoutDashboard size={22} />, desc: "Enrollment, certificates & hiring pipeline", testid: "demo-admin" },
  { role: "FSCD Super Admin", email: "superadmin@demo.org", icon: <ShieldCheck size={22} />, desc: "Global dashboard, countries & verticals", testid: "demo-superadmin" },
  { role: "Employer / Partner", email: "employer@demo.org", icon: <Building2 size={22} />, desc: "Browse certified, job-ready candidates", testid: "demo-employer" },
];

const LEADERS = [
  { name: "Angela K. Pitwood", role: "VP, Medical, Technical & Regulatory Affairs", bio: "Healthcare professional with 30+ years of pharmaceutical-industry experience in clinical trials and product vigilance — including global product-safety surveillance operations at Pfizer across the US, Europe and Asia. Co-founded IPM Safety Services.", edu: "Nursing (Nottingham) · PG Dip. Pharmacovigilance (Hertford) · MBA (Baruch)" },
  { name: "Milind Desai", role: "VP & CFO, India Operations", bio: "Chartered Accountant with deep experience in corporate finance and operations in India: CFO of IndiPharm India, Group Manager at ICICI Securities (JV with J.P. Morgan), and SVP Corporate Finance at Tata Finance — including INR 3 billion in wind-power financing.", edu: "Chartered Accountant · B.Com & LL.B. (University of Bombay)" },
  { name: "Robert Bruch, DBA", role: "Foundation Management & Strategic Development", bio: "Contributes to Foundation coordination, strategic development, partnership discussions and organizational planning.", edu: "Doctor of Business Administration" },
];

const FAQS = [
  ["Is FSCD a registered charity?", "Yes. FSCD is an IRS-approved 501(c)(3) public charity (approved April 14, 2026). Federal EIN 41-4092047."],
  ["What does the certificate qualify me for?", "Certificates are internal FSCD qualifications — they qualify you to work, volunteer or intern with FSCD programs such as vision centers and community clinics. They are not externally accredited licenses, and we say so on every credential."],
  ["Can I learn without a reliable internet connection?", "Yes. Lessons are saved on your device once opened, you can complete them offline, and your progress syncs when you reconnect. The platform is built mobile-first for low-bandwidth communities."],
  ["Who reviews my practical video submissions?", "Qualified FSCD assessors review and score every submission against a rubric (technique, communication, clarity) and either approve it or request a resubmission with feedback."],
  ["How do employers verify a certificate?", "Every certificate carries a unique ID and QR code. Scanning it — or entering the ID on the public verify page — instantly confirms the credential, the track and the issue date."],
  ["Is the Financial Skills Lab real trading?", "No. All financial modules are educational simulations only. No real money is ever involved."],
];

const ACTIONS = [
  { icon: <HandHeart size={24} />, title: "Volunteer", desc: "Certified graduates and professionals can serve at vision centers, camps and training sites.", cta: "Join as Volunteer", href: "#demo" },
  { icon: <HeartHandshake size={24} />, title: "Donate", desc: "Fund a vision center, a solar installation, or a trainee's certification pathway.", cta: "Support a Program", href: "mailto:info@thefscd.org" },
  { icon: <Building2 size={24} />, title: "Partner", desc: "Hospitals, universities, funders and technology providers — build programs with us.", cta: "Partner With FSCD", href: "mailto:info@thefscd.org" },
];

const SectionTag = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">{children}</p>
);

export default function Landing() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("reveal-visible"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.08 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const demoLogin = async (email, testid) => {
    setBusy(testid);
    try {
      const u = await login(email, "Demo123!");
      navigate(ROLE_HOME[u.role] || "/app");
    } catch (e) { toast.error("Demo login failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF6] text-slate-800" style={{ fontFamily: '"Montserrat", "Noto Sans Devanagari", sans-serif' }}>
      <div className="bg-teal-900 text-teal-100 text-xs py-2 px-4 text-center">Community-led development · Healthcare · Clean energy · Local capacity · <a href="mailto:info@thefscd.org" className="underline">info@thefscd.org</a></div>

      <header className="sticky top-0 z-50 bg-[#FDFBF6]/95 backdrop-blur border-b border-teal-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo size={44} />
          <div className="flex items-center gap-2">
            <a href="#what-we-do" className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-teal-800 px-2">What We Do</a>
            <a href="#mission" className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-teal-800 px-2">Mission</a>
            <a href="#vision-screening" className="hidden md:inline text-sm font-semibold text-slate-600 hover:text-teal-800 px-2">Vision Centers</a>
            <a href="#leadership" className="hidden lg:inline text-sm font-semibold text-slate-600 hover:text-teal-800 px-2">Leadership</a>
            <RegionSwitcher />
            <ShareApp compact />
            <a href="#demo" data-testid="nav-explore-demo" className="hidden sm:inline-flex h-10 px-4 items-center rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold transition-colors">Explore Demo</a>
            {user && user !== false ? (
              <button data-testid="nav-dashboard-btn" onClick={() => navigate(ROLE_HOME[user.role] || "/app")} className="h-10 px-4 rounded-full border border-teal-300 text-teal-900 text-sm font-semibold hover:bg-teal-50">Dashboard</button>
            ) : (
              <Link to="/login" data-testid="nav-login-btn" className="h-10 px-4 flex items-center rounded-full border border-teal-300 text-teal-900 text-sm font-semibold hover:bg-teal-50">Login</Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section data-reveal="true" className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-200/40 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6 fade-in">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full"><ShieldCheck size={13} /> 501(c)(3) Public Charity · EIN 41-4092047</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-teal-950">
              Building <span className="text-teal-600">Self-Reliant</span> Communities.
            </h1>
            <p className="text-slate-600 max-w-lg text-base leading-relaxed">
              FSCD is a nonprofit foundation that brings <span className="font-semibold text-teal-900">vision screening centers</span>, <span className="font-semibold text-teal-900">clean energy for clinics and schools</span>, and <span className="font-semibold text-teal-900">job training & certification</span> to rural and underserved communities across South & Southeast Asia — starting in India.
            </p>
            <p className="text-slate-500 text-sm max-w-lg">We don't just deliver services. We train local people, certify them, and hand them the keys — so the change keeps working after we leave.</p>
            <div className="flex flex-wrap gap-3">
              <a href="#what-we-do" data-testid="hero-explore-programs-btn" className="h-12 px-6 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2 transition-colors">What We Do <ArrowRight size={18} /></a>
              <Link to="/login" data-testid="hero-student-login-btn" className="h-12 px-6 rounded-full bg-teal-950 text-white font-semibold flex items-center hover:bg-teal-900 transition-colors">Student Login</Link>
              <a href="#demo" data-testid="hero-partner-btn" className="h-12 px-6 rounded-full border border-teal-300 text-teal-900 font-semibold flex items-center hover:bg-teal-50 transition-colors">Partner With FSCD</a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500 pt-1">
              <span className="flex items-center gap-1.5"><Globe2 size={14} className="text-teal-600" /> South & Southeast Asia · Initial focus: India</span>
              <span className="flex items-center gap-1.5"><Award size={14} className="text-amber-600" /> Verifiable Certificates</span>
              <span className="flex items-center gap-1.5"><HandHeart size={14} className="text-emerald-600" /> Women's Economic Empowerment</span>
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              <img src={IMG.screening} alt="Vision screening for children" className="rounded-3xl object-cover h-44 sm:h-56 w-full border-4 border-white shadow-lg rotate-1" />
              <img src={IMG.physician} alt="Indian physician" className="rounded-3xl object-cover h-44 sm:h-56 w-full border-4 border-white shadow-lg -rotate-1 mt-6" />
              <img src={IMG.classroom} alt="Students learning" className="rounded-3xl object-cover h-44 sm:h-56 w-full border-4 border-white shadow-lg -rotate-1" />
              <img src={IMG.team} alt="Healthcare team" className="rounded-3xl object-cover h-44 sm:h-56 w-full border-4 border-white shadow-lg rotate-1 mt-6" />
            </div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white border border-teal-100 rounded-2xl px-5 py-3 shadow-xl flex items-center gap-3">
              <CheckCircle2 className="text-emerald-600" size={22} />
              <div><p className="font-heading font-bold text-teal-950 text-sm">Certificate Issued</p><p className="text-[11px] text-slate-500">FSCD-IND-2026-8849 · Verified ✓</p></div>
            </div>
            <div className="absolute -top-4 -left-4 bg-teal-700 text-white rounded-2xl px-4 py-2.5 shadow-xl -rotate-2 flex items-center gap-2">
              <Camera size={16} /><div><p className="text-xs font-bold leading-tight">Vision Center</p><p className="text-[10px] text-teal-200">Eyer2 portable imaging</p></div>
            </div>
            <div className="absolute top-1/2 -right-3 bg-amber-400 text-teal-950 rounded-2xl px-4 py-2.5 shadow-xl rotate-2 hidden sm:flex items-center gap-2">
              <Zap size={16} /><div><p className="text-xs font-bold leading-tight">Solar-powered</p><p className="text-[10px]">clinics & schools</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-teal-700 text-white overflow-hidden py-3">
        <div className="flex gap-8 whitespace-nowrap text-sm font-semibold animate-[marquee_25s_linear_infinite]" style={{ width: "max-content" }}>
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => <span key={i} className="flex items-center gap-8">{m} <span className="text-amber-300">✦</span></span>)}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.33%); } }`}</style>
      </div>

      {/* What we do */}
      <section data-reveal="true" id="what-we-do" className="max-w-6xl mx-auto px-4 py-16 space-y-8">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <SectionTag>What We Do</SectionTag>
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950">Three Programs. One Goal: Communities That Run Themselves.</h2>
          <p className="text-slate-500">Access. Capacity. Sustainability. Each program removes a barrier — together they form a system a community can own.</p>
        </div>
        <div className="grid lg:grid-cols-3 gap-4" data-testid="pillars">
          {PILLARS.map((p, i) => (
            <div key={p.title} className="rounded-3xl border border-teal-100 bg-white p-6 space-y-4 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all relative overflow-hidden">
              <span className="absolute top-4 right-5 font-heading text-5xl font-extrabold text-teal-50">{`0${i + 1}`}</span>
              <span className={`w-14 h-14 rounded-2xl flex items-center justify-center border ${p.tint}`}>{p.icon}</span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{p.badge}</p>
                <h3 className="font-heading font-bold text-teal-950 text-lg leading-snug mt-1">{p.title}</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm font-semibold text-teal-800 bg-teal-50 border border-teal-100 rounded-2xl px-5 py-4 max-w-3xl mx-auto">
          Learn → Practice → Certify → Get Employed → Train Others → Strengthen Communities. This platform is where FSCD's workforce is trained, certified and hired.
        </p>
      </section>

      {/* Stats + partners */}
      <section data-reveal="true" className="bg-white border-y border-teal-100">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-3 lg:grid-cols-6 gap-6" data-testid="impact-stats">
          {STATS.map(([n, l]) => (
            <div key={l} className="text-center">
              <p className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-800">{n}</p>
              <p className="text-[11px] sm:text-xs text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">In collaboration with</span>
          {PARTNERS.map((p) => <span key={p} className="text-sm font-heading font-bold text-slate-300 hover:text-teal-600 transition-colors cursor-default">{p}</span>)}
        </div>
      </section>

      {/* Punch band 1 */}
      <section data-reveal="true" className="bg-teal-950 text-white overflow-hidden relative">
        <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 py-14 text-center space-y-3">
          <h2 className="font-heading text-3xl sm:text-5xl font-extrabold leading-tight">We Don't Ship Equipment.<br /><span className="text-amber-400">We Build Owners.</span></h2>
          <p className="text-teal-200 text-sm max-w-xl mx-auto">Every FSCD program ends the same way: trained, certified local people holding the keys — and the skills to keep them.</p>
        </div>
      </section>

      {/* Mission framework */}
      <section data-reveal="true" id="mission" className="max-w-6xl mx-auto px-4 py-16 space-y-8">
        <div className="max-w-3xl space-y-3">
          <SectionTag>Foundation Framework</SectionTag>
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950">Vision. Mission. Purpose. Recognized Public Charity.</h2>
          <p className="text-slate-500">A foundation designed to strengthen what already exists — our role is not to replace local leadership, it is to strengthen it.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="mission-framework">
          {FRAMEWORK.map((f) => (
            <div key={f.no} className="bg-white rounded-3xl border border-teal-100 p-6 space-y-2 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <span className="font-heading text-2xl font-extrabold text-amber-500">{f.no}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-600">{f.label}</span>
              </div>
              <h3 className="font-heading font-bold text-teal-950 text-lg">{f.title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Six areas */}
      <section data-reveal="true" className="bg-emerald-50/60 border-y border-emerald-100">
        <div className="max-w-6xl mx-auto px-4 py-14 space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <SectionTag>Program Framework</SectionTag>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Six Connected Areas of Community Development.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="six-areas">
            {SIX_AREAS.map((a) => (
              <div key={a.t} className="bg-white rounded-2xl border border-emerald-100 p-5 space-y-2 shadow-sm hover:-translate-y-0.5 transition-transform">
                <span className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">{a.icon}</span>
                <p className="font-heading font-bold text-teal-950 text-sm">{a.t}</p>
                <p className="text-xs text-slate-500">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision screening flagship */}
      <section data-reveal="true" id="vision-screening" className="max-w-6xl mx-auto px-4 py-16 space-y-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <SectionTag>Flagship Program · Vision Screening Initiative</SectionTag>
            <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950">Bring Advanced Eye Imaging Closer to the Patient.</h2>
            <p className="text-slate-500 leading-relaxed">Communities can be far from specialists — but portable Eyer2 image capture, connected clinical review, referral pathways and <span className="font-semibold text-teal-900">local training</span> bring the beginning of a care pathway to the patient. 55° retinal imaging, anterior-segment modes, and EyerCloud connected review.</p>
            <div className="grid grid-cols-2 gap-3">
              {[["Capture", "High-quality retinal images at the point of care"], ["Screen", "Software-assisted prioritization supports the workflow"], ["Review", "Qualified clinicians interpret every finding"], ["Act", "Referral, glasses, treatment and follow-up"]].map(([t, d], i) => (
                <div key={t} className="bg-white rounded-2xl border border-teal-100 p-4">
                  <p className="font-heading font-bold text-teal-900 text-sm">{i + 1}. {t}</p>
                  <p className="text-xs text-slate-500 mt-1">{d}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Screening technology supports triage; diagnosis and treatment remain with qualified healthcare professionals. Trained locally on this platform: Eye Technicians and Patient Care Coordinators staff every vision center.</p>
          </div>
          <div className="space-y-3">
            <img src={IMG.eyerEco} alt="Eyer2 connected eye-imaging ecosystem" onError={(e) => { e.currentTarget.src = IMG.screening; }} className="rounded-3xl w-full object-cover bg-white border border-teal-100 shadow-lg" />
            <div className="grid grid-cols-2 gap-3">
              <img src={IMG.eyerElder} alt="Eyer2 retinal examination with an older adult" onError={(e) => { e.currentTarget.src = IMG.physician; }} className="rounded-2xl h-36 w-full object-cover border-4 border-white shadow-md" />
              <img src={IMG.retina} alt="Retinal image captured with Eyer2" onError={(e) => { e.currentTarget.src = IMG.team; }} className="rounded-2xl h-36 w-full object-cover border-4 border-white shadow-md" />
            </div>
          </div>
        </div>
        {/* Clean energy band */}
        <div className="relative rounded-3xl overflow-hidden">
          <img src={IMG.solar} alt="Solar energy infrastructure" className="w-full h-56 sm:h-64 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-950/90 via-teal-900/70 to-transparent" />
          <div className="absolute inset-0 p-6 sm:p-10 flex flex-col justify-center max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">Flagship Program · Clean Energy Access</p>
            <h3 className="font-heading text-xl sm:text-3xl font-extrabold text-white mt-1">Reliable Energy. Stronger Communities.</h3>
            <p className="text-teal-100 text-sm mt-2">Solar power for clinics, schools and vision centers — cold chain, medical equipment, connectivity — with local operators trained and certified right here.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {["Generate", "Stabilize", "Enable", "Sustain"].map((s) => <span key={s} className="text-xs font-semibold bg-white/15 border border-white/20 text-white px-3 py-1.5 rounded-full">{s}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Course showcase */}
      <section data-reveal="true" className="bg-white border-y border-teal-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 space-y-6">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <SectionTag><span className="flex items-center gap-1.5"><Sparkles size={13} /> The Workforce Behind the Programs</span></SectionTag>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950 mt-1">12 Courses. Built to Staff Real Programs.</h2>
              <p className="text-slate-500 text-sm mt-1">Every vision center, solar installation and clinic needs trained people. This is where they're trained.</p>
            </div>
            <a href="#demo" className="text-sm text-teal-700 font-semibold flex items-center gap-1">Try them in the demo <ChevronRight size={16} /></a>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4" data-testid="course-showcase" style={{ scrollbarWidth: "thin" }}>
            {COURSES.map((c) => (
              <div key={c.title} className="w-56 shrink-0 rounded-2xl border border-teal-100 bg-[#FDFBF6] overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="relative h-28">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover" />
                  <span className={`absolute top-2 left-2 text-[10px] font-bold px-2.5 py-1 rounded-full ${c.live ? "bg-emerald-500 text-white" : "bg-white/90 text-slate-600"}`}>{c.live ? "ENROLLING NOW" : "COMING SOON"}</span>
                </div>
                <div className="p-3.5 space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-600">{c.vertical}</p>
                  <p className="font-heading font-bold text-teal-950 text-sm leading-snug">{c.title}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {c.weeks} weeks · Certificate</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Punch band 2 */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-12">
        <div className="rounded-3xl border-2 border-teal-950 bg-amber-50 p-8 sm:p-12 text-center relative overflow-hidden">
          <span className="absolute -left-4 -top-6 font-heading text-[120px] font-extrabold text-amber-200/60 select-none">"</span>
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950 leading-tight relative">A Certificate Here Isn't Paper.<br />It's a Job at a Vision Center.</h2>
          <p className="text-slate-600 text-sm mt-3 max-w-xl mx-auto relative">Graduates don't just finish a course — they staff FSCD's own programs: screening eyes, coordinating patients, running front desks, maintaining solar systems.</p>
          <div className="flex flex-wrap justify-center gap-2 mt-5 relative">
            {["Learn It", "Prove It", "Get Certified", "Get Hired", "Teach It Forward"].map((s, i) => (
              <span key={s} className="flex items-center gap-2 text-xs font-bold bg-white border border-teal-200 text-teal-900 px-3.5 py-2 rounded-full">{i + 1}. {s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-16 space-y-8">
        <div className="max-w-3xl space-y-3">
          <SectionTag>Our Approach</SectionTag>
          <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950">From Local Need to Sustainable Capacity.</h2>
          <p className="text-slate-500">A repeatable path from a demonstrated community need to a locally owned solution.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="approach-steps">
          {APPROACH.map(([t, d], i) => (
            <div key={t} className="bg-white rounded-2xl border border-teal-100 p-5 flex gap-4 shadow-sm hover:border-teal-300 transition-colors">
              <span className="w-10 h-10 rounded-full bg-teal-700 text-white flex items-center justify-center font-heading font-bold shrink-0">{i + 1}</span>
              <div><p className="font-heading font-bold text-teal-950">{t}</p><p className="text-sm text-slate-500 mt-1">{d}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Transformation Chain */}
      <section data-reveal="true" className="bg-teal-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 space-y-10">
          <div className="text-center space-y-2 max-w-3xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-400">The Transformation Chain</p>
            <h2 className="font-heading text-2xl sm:text-4xl font-extrabold">A Life Changes When Access Becomes Ability.</h2>
            <p className="text-teal-200 text-sm">From not being able to see, to participating in school. From unreliable electricity, to dependable local care. From a project, to community ownership.</p>
          </div>
          {CHAIN.map((c, i) => (
            <div key={c.no} className={`grid lg:grid-cols-2 gap-8 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <img src={c.img} alt={c.title} className="rounded-3xl object-cover h-64 w-full border border-white/10 shadow-2xl" />
              <div className="space-y-4">
                <p className="text-xs font-mono font-semibold text-amber-400">{c.no}</p>
                <h3 className="font-heading text-xl sm:text-2xl font-extrabold leading-snug">{c.title}</h3>
                <p className="text-teal-100 text-sm leading-relaxed">{c.body}</p>
                <div className="flex flex-wrap gap-2">
                  {c.tags.map((t) => <span key={t} className="text-xs font-semibold bg-white/10 border border-white/15 px-3 py-1.5 rounded-full">{t}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section data-reveal="true" id="leadership" className="bg-white border-y border-teal-100">
        <div className="max-w-6xl mx-auto px-4 py-16 space-y-8">
          <div className="max-w-3xl space-y-3">
            <SectionTag>Management Team</SectionTag>
            <h2 className="font-heading text-2xl sm:text-4xl font-extrabold text-teal-950">Experience Behind the Mission.</h2>
            <p className="text-slate-500">Sustainable development, international finance, healthcare and clinical operations, regulatory affairs, clean energy and community development.</p>
          </div>
          <div className="rounded-3xl border border-teal-100 bg-[#FDFBF6] p-6 sm:p-8 grid lg:grid-cols-[auto,1fr] gap-6 shadow-sm" data-testid="leader-founder">
            <div className="flex lg:flex-col items-center lg:items-start gap-4">
              <span className="w-20 h-20 rounded-2xl bg-teal-700 text-white flex items-center justify-center font-heading text-3xl font-extrabold shrink-0">DL</span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Founder & Organizational Leadership</p>
                <h3 className="font-heading font-extrabold text-teal-950 text-xl">Dilip R. Limaye</h3>
                <p className="text-xs text-slate-500 mt-1">Sustainable Development · Finance · Clean Energy · International Partnerships</p>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">A highly accomplished entrepreneur and senior executive with more than 40 years of international success financing and managing sustainability programs — advanced energy efficiency, renewable energy, sustainable water services and clinical trials — serving organizations in more than 40 countries.</p>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {["Senior Advisor to the World Bank on 50+ sustainable-development projects", "Advisor to UNDP, UNEP, IFC, ADB, USAID, IEA and KfW", "Co-developer of a 683 MW solar PV project in Texas", "Co-author/editor of 9 books · 50+ technical papers"].map((h) => (
                  <p key={h} className="flex items-start gap-2 text-slate-600"><CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" /> {h}</p>
                ))}
              </div>
              <p className="text-xs text-slate-400">IIT Bombay (B.Tech Hons, President of India Gold Medal) · Cornell (M.S.) · Wharton (Ph.D. candidate) · Harvard Executive Program, Global Climate Change & Sustainable Development</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="leaders-grid">
            {LEADERS.map((l) => (
              <div key={l.name} className="bg-[#FDFBF6] rounded-2xl border border-teal-100 p-5 space-y-3 hover:shadow-md transition-shadow">
                <span className="w-12 h-12 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center font-heading font-extrabold">{l.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                <div>
                  <p className="font-heading font-bold text-teal-950">{l.name}</p>
                  <p className="text-xs font-semibold text-amber-700 mt-0.5">{l.role}</p>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">{l.bio}</p>
                <p className="text-[11px] text-slate-400">{l.edu}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-14 space-y-6">
        <div className="text-center space-y-1">
          <SectionTag>Voices From the Field</SectionTag>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Real People. Real Progress.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4" data-testid="testimonials">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl border border-teal-100 p-5 space-y-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex gap-0.5">{Array.from({ length: t.stars }).map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}</div>
              <p className="text-sm text-slate-600 leading-relaxed">“{t.quote}”</p>
              <div className="flex items-center gap-3 pt-1">
                <span className="w-9 h-9 rounded-full bg-teal-700 text-white flex items-center justify-center font-heading font-bold text-sm">{t.name[0]}</span>
                <div><p className="font-semibold text-teal-950 text-sm">{t.name}</p><p className="text-[11px] text-slate-400">{t.role}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Certificate + centers */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-4">
          <SectionTag>Verifiable Credentials</SectionTag>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Every Certificate Can Be Verified in Seconds.</h2>
          <p className="text-slate-500 text-sm">Each graduate receives a unique credential ID and QR code. Employers and partners scan it — or type it into the public verify page — to confirm the qualification instantly. Certificates are internal FSCD qualifications, stated clearly on every credential.</p>
          <div className="space-y-3 pt-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Training centers — India</p>
            <div className="grid grid-cols-2 gap-3" data-testid="centers-grid">
              {CENTERS.map((c) => (
                <div key={c.city} className="bg-white rounded-2xl border border-teal-100 p-4 hover:border-teal-300 transition-colors">
                  <p className="flex items-center gap-1.5 text-teal-700 font-semibold text-sm"><MapPin size={14} /> {c.city}</p>
                  <p className="text-xs text-slate-500 mt-1">{c.name}</p>
                  <p className="text-[11px] text-emerald-700 font-semibold mt-1">{c.programs} programs · Active</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-100 to-amber-100 rounded-3xl rotate-2" />
          <div className="relative bg-white rounded-3xl border-2 border-teal-950 p-6 shadow-xl space-y-4" data-testid="certificate-preview">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LogoMark size={34} />
                <span className="font-heading font-bold text-teal-950 text-sm">FSCD Global Skills</span>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full">Internal FSCD Qualification</span>
            </div>
            <div className="text-center py-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Certificate of Completion</p>
              <p className="font-heading text-2xl font-extrabold text-teal-950 mt-1">Priya Sharma</p>
              <p className="text-sm text-slate-500">Eye Technician Fundamentals</p>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
              <div className="text-xs text-slate-500 space-y-1">
                <p>ID: <span className="font-mono font-semibold text-teal-900">FSCD-IND-2026-8849</span></p>
                <p>Issued: June 2026 · Verified ✓</p>
              </div>
              <div className="text-center">
                <QRCodeSVG value="https://academy-hire.preview.emergentagent.com/verify/FSCD-IND-2026-8849" size={72} />
                <p className="text-[9px] text-slate-400 mt-1">Scan to verify</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Punch band 3 */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-12 text-center space-y-4">
        <h2 className="font-heading text-3xl sm:text-5xl font-extrabold leading-tight text-teal-950">Charity Fades.<br /><span className="text-teal-600">Capability Compounds.</span></h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">One trained eye technician screens thousands. One certified trainer builds the next cohort. One solar-powered clinic never closes for a blackout. That's the math we invest in.</p>
        <div className="flex flex-wrap justify-center gap-6 pt-2">
          {[["1 technician", "→ 1,000s screened"], ["1 trainer", "→ next 20 trainees"], ["1 solar clinic", "→ care that stays on"]].map(([a, b]) => (
            <div key={a} className="text-center">
              <p className="font-heading text-xl font-extrabold text-teal-800">{a}</p>
              <p className="text-xs text-amber-700 font-semibold">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Global vision */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-14 space-y-5">
        <SectionTag>Where We're Going</SectionTag>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Our Global Vision</h2>
        <div className="flex flex-wrap items-center gap-2" data-testid="global-vision">
          {REGIONS.map((r, i) => (
            <div key={r} className="flex items-center gap-2">
              <span className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold border ${i === 0 ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-600 border-teal-100"}`}>
                <MapPin size={14} /> {r}{i === 0 && <span className="text-[10px] bg-white/25 rounded-full px-2 py-0.5 ml-1">LIVE</span>}
              </span>
              {i < REGIONS.length - 1 && <ChevronRight size={16} className="text-slate-300" />}
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 max-w-3xl">Across South and Southeast Asia, with an initial focus on India. Curricula, languages, currencies, certifications, training centers and partner organizations are configurable per country.</p>
      </section>

      {/* Get involved */}
      <section data-reveal="true" className="max-w-6xl mx-auto px-4 py-14 space-y-6">
        <div className="text-center space-y-2">
          <SectionTag>Get Involved</SectionTag>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Three Ways to Change What a Community Can Sustain.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4" data-testid="get-involved">
          {ACTIONS.map((a) => (
            <div key={a.title} className="rounded-3xl border border-teal-100 bg-white p-6 space-y-3 text-center shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
              <span className="w-14 h-14 mx-auto rounded-2xl bg-teal-50 text-teal-700 border border-teal-100 flex items-center justify-center">{a.icon}</span>
              <p className="font-heading font-bold text-teal-950 text-lg">{a.title}</p>
              <p className="text-sm text-slate-500">{a.desc}</p>
              <a href={a.href} className="inline-flex h-10 px-5 items-center rounded-full bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors">{a.cta}</a>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section data-reveal="true" className="bg-white border-y border-teal-100">
        <div className="max-w-4xl mx-auto px-4 py-14 space-y-6">
          <div className="text-center space-y-2">
            <SectionTag>Common Questions</SectionTag>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-teal-950">Answers Before You Ask.</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map(([q, a]) => (
              <details key={q} className="group bg-[#FDFBF6] rounded-2xl border border-teal-100 open:border-teal-300 transition-colors">
                <summary className="flex items-center justify-between gap-3 cursor-pointer list-none px-5 py-4 font-semibold text-teal-950 text-sm">
                  {q}
                  <ChevronRight size={18} className="text-teal-600 shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-500 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Demo */}
      <section data-reveal="true" id="demo" className="bg-teal-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 space-y-8">
          <div className="text-center space-y-2">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Explore the Demo</h2>
            <p className="text-teal-200">One click to enter any role. All demo passwords: <span className="font-mono text-amber-300">Demo123!</span></p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {DEMOS.map((d) => (
              <button key={d.role} data-testid={d.testid} onClick={() => demoLogin(d.email, d.testid)} disabled={!!busy}
                className="text-left rounded-2xl border border-white/15 bg-white/[0.05] p-5 space-y-3 hover:border-amber-400/70 hover:bg-white/[0.1] transition-all disabled:opacity-50">
                <span className="w-11 h-11 rounded-xl bg-amber-400/15 text-amber-300 border border-amber-400/30 flex items-center justify-center">
                  {busy === d.testid ? <Loader2 className="animate-spin" size={20} /> : d.icon}
                </span>
                <div>
                  <p className="font-heading font-bold">{d.role}</p>
                  <p className="text-xs text-teal-200 mt-1">{d.desc}</p>
                </div>
                <p className="text-[11px] font-mono text-teal-300/60">{d.email}</p>
              </button>
            ))}
          </div>
          <div className="text-center">
            <p className="font-heading text-lg font-bold">Don't Fund a Moment. Build What Lasts.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <a href="mailto:info@thefscd.org" className="h-11 px-5 rounded-full bg-amber-400 text-teal-950 text-sm font-bold flex items-center hover:bg-amber-300">Start a Conversation</a>
              <a href="#demo" className="h-11 px-5 rounded-full border border-white/25 text-sm font-semibold flex items-center hover:bg-white/10">Become a Strategic Partner</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#FDFBF6]">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-2 text-sm text-slate-400">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <p>© 2026 Foundation for Sustainable Community Development · 501(c)(3) Public Charity · EIN 41-4092047 · info@thefscd.org</p>
            <p>Education. Skills. Opportunity. Community.</p>
          </div>
          <p className="text-xs">Certificates are internal FSCD qualifications. Financial modules are educational simulations only. Screening technology supports triage; clinical interpretation remains with qualified professionals.</p>
        </div>
      </footer>
    </div>
  );
}
